import { z } from 'zod'

export const backtestMetricsSchema = z.object({
  roundTrips: z.number().int(),
  wins: z.number().int(),
  winRate: z.number(),
  netPnl: z.number(),
  afterTaxNet: z.number(),
  totalCommissions: z.number(),
  maxCapitalLocked: z.number(),
  maxHoldMs: z.number(),
  // mean hold-cycle duration in ms
  avgHoldMs: z.number(),
  openLotsAtEnd: z.number().int(),
  // per-instrument price stats over the run window (null/omitted for the
  // aggregate row, where mixing instruments' prices is meaningless)
  priceMin: z.number().nullable().optional(),
  priceMax: z.number().nullable().optional(),
  // time-weighted average price (mean of per-minute closes)
  avgPrice: z.number().nullable().optional(),
})

export type BacktestMetricsDto = z.infer<typeof backtestMetricsSchema>

export const dailyEquityPointSchema = z.object({
  date: z.string(),
  equity: z.number(),
})

export type DailyEquityPointDto = z.infer<typeof dailyEquityPointSchema>

export const ledgerEntrySchema = z.object({
  side: z.enum(['BUY', 'SELL']),
  price: z.number(),
  lots: z.number().int(),
  shares: z.number().int(),
  commission: z.number(),
  timestamp: z.coerce.date(),
  realizedNet: z.number().nullable(),
})

export type LedgerEntryDto = z.infer<typeof ledgerEntrySchema>

export type BreakdownNodeDto = {
  key: string
  label: string
  level: 'year' | 'month' | 'week' | 'day'
  net: number
  afterTax: number
  // closed round-trips (a buy through to the position being fully closed)
  trades: number
  // total order count (every buy and sell), including days with no closed trade
  orders: number
  winRate: number
  children?: BreakdownNodeDto[]
  perInstrument?: {
    instrumentUid: string
    ticker: string
    name: string
    net: number
    trades: number
    orders: number
  }[]
}

export const breakdownNodeSchema: z.ZodType<BreakdownNodeDto> = z.lazy(() =>
  z.object({
    key: z.string(),
    label: z.string(),
    level: z.enum(['year', 'month', 'week', 'day']),
    net: z.number(),
    afterTax: z.number(),
    trades: z.number().int(),
    orders: z.number().int(),
    winRate: z.number(),
    children: z.array(breakdownNodeSchema).optional(),
    perInstrument: z
      .array(
        z.object({
          instrumentUid: z.string(),
          ticker: z.string(),
          name: z.string(),
          net: z.number(),
          trades: z.number().int(),
          orders: z.number().int(),
        })
      )
      .optional(),
  })
)

export const perInstrumentResultSchema = z.object({
  instrumentUid: z.string(),
  ticker: z.string(),
  name: z.string(),
  metrics: backtestMetricsSchema,
  ledger: z.array(ledgerEntrySchema),
  dailyEquity: z.array(dailyEquityPointSchema),
})

export type PerInstrumentResultDto = z.infer<typeof perInstrumentResultSchema>

export const excludedInstrumentSchema = z.object({
  instrumentUid: z.string(),
  ticker: z.string(),
  name: z.string(),
  reason: z.string(),
})

export type ExcludedInstrumentDto = z.infer<typeof excludedInstrumentSchema>

export const backtestResultSchema = z.object({
  perInstrument: z.array(perInstrumentResultSchema),
  aggregate: z.object({
    metrics: backtestMetricsSchema,
    dailyEquity: z.array(dailyEquityPointSchema),
    breakdown: breakdownNodeSchema,
  }),
  excluded: z.array(excludedInstrumentSchema).default([]),
})

export type BacktestResultDto = z.infer<typeof backtestResultSchema>

// matched-entry summary attached to a SELL event (FIFO lineage derived from the
// ledger): when the closed lots were first bought, their lot-weighted entry
// price, and how long they were held. null on BUY events.
export const tradeEntryLineageSchema = z.object({
  timestamp: z.coerce.date(),
  price: z.number(),
  holdMs: z.number().int(),
})

export type TradeEntryLineageDto = z.infer<typeof tradeEntryLineageSchema>

export const dayDetailTradeSchema = ledgerEntrySchema.extend({
  entry: tradeEntryLineageSchema.nullable(),
})

export type DayDetailTradeDto = z.infer<typeof dayDetailTradeSchema>

// lots still open at the end of the selected day (FIFO leftover)
export const openPositionSchema = z.object({
  lots: z.number().int(),
  avgPrice: z.number(),
  since: z.coerce.date(),
})

export type OpenPositionDto = z.infer<typeof openPositionSchema>

export const dayDetailSchema = z.object({
  candles: z.array(
    z.object({
      time: z.coerce.date(),
      open: z.number(),
      close: z.number(),
      high: z.number(),
      low: z.number(),
      volume: z.number(),
    })
  ),
  events: z.array(dayDetailTradeSchema),
  openPosition: openPositionSchema.nullable(),
})

export type DayDetailDto = z.infer<typeof dayDetailSchema>
